<template>
  <BuilderWrapper
    title="Popup"
    helpful-tip="The map popup shows the geometry name, pie chart (if enabled), the bubble total and numeric popup items aggregated appropriately (if any). In addition, an optional count can be included after the total with a custom label."
  >
    <LabelledInput>
      <b-checkbox v-model="showProjectCount">
        Show Project Count
      </b-checkbox>
    </LabelledInput>

    <template v-if="showProjectCount">
      <LabelledInput
        label="Plural:"
        label-cols="3"
        label-align="right"
      >
        <b-input v-model="projectPlural" />
      </LabelledInput>

      <LabelledInput
        label="Singular:"
        label-cols="3"
        label-align="right"
      >
        <b-input v-model="projectSingular" />
      </LabelledInput>
    </template>

    <LabelledInput
      label="Total fields:"
      helpful-tip="By default, total is summed by the total fields in the Fields Settings"
    >
      <b-checkbox-group
        v-model="mapPopupTotalItems"
        :options="numericalCategoryOptions"
      />
    </LabelledInput>

    <MapPopup
      :graph-id="graphId"
      :title="title"
      :popup-details="popupDetails"
    />
  </BuilderWrapper>
</template>

<script lang="ts">

import Vue from 'vue'
import Component from 'vue-class-component'

import BuilderWrapper from '@/components/builder/UI/BuilderWrapper.vue'
import LabelledInput from '@/components/builder/UI/LabelledInput.vue'
import MapPopup from '@/components/public/Popup/MapPopup.vue'
// @ts-ignore uuid not having types
import { v4 as uuidv4 } from 'uuid'

import { APP_CONFIG_KEY, MAP_POPUP_TOTAL_ITEMS_CONFIG_KEY } from '@/constants'
import { MapPopupDetails, VisNode } from '@/types'
import _cloneDeep from 'lodash/cloneDeep'

const components = {
  BuilderWrapper,
  LabelledInput,
  MapPopup,
}

@Component({ components })
export default class MapPopupSettings extends Vue {
  graphId = 'map-popup-settings-' + uuidv4()
  title = 'Example'

  get showProjectCount () {
    return this.$typedStore.primary.app.config[APP_CONFIG_KEY].showProjectCount
  }

  set showProjectCount (value) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [APP_CONFIG_KEY, 'showProjectCount'],
      value,
    })
  }

  get projectPlural () {
    return this.$typedStore.primary.app.config[APP_CONFIG_KEY].projectPlural
  }

  set projectPlural (value) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [APP_CONFIG_KEY, 'projectPlural'],
      value,
    })
  }

  get projectSingular () {
    return this.$typedStore.primary.app.config[APP_CONFIG_KEY].projectSingular
  }

  set projectSingular (value) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [APP_CONFIG_KEY, 'projectSingular'],
      value,
    })
  }

  get mapPopupTotalItems () {
    return this.$typedStore.primary.app.config.mapPopupTotalItems
  }

  set mapPopupTotalItems (value) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [MAP_POPUP_TOTAL_ITEMS_CONFIG_KEY],
      value,
    })
  }

  get monetaryFields () {
    return this.$typedStore.visualisationApp.monetaryFields
  }

  get numericFields () {
    return this.$typedStore.visualisationApp.numericFields
  }

  get numericalFields () {
    return [
      ...this.monetaryFields,
      ...this.numericFields,
    ]
  }

  get numericalCategoryOptions () {
    return this.numericalFields.map(({ name, column }) => ({ text: name, value: column }))
  }

  get popupNode (): VisNode {
    return this.$typedStore.visualisationData.firstLeafVisNode
  }

  get popupDetails (): MapPopupDetails {
    const node = _cloneDeep(this.popupNode)
    node.totalString = this.totalString
    return {
      node,
      projectCount: 3,
    }
  }

  get totalString () {
    const leafNodes = [this.popupNode]

    let sumTotalsByUnit = this.$typedStore.visualisationApp.sumTotalsByUnit({ leafNodes })
    if (this.mapPopupTotalItems && this.mapPopupTotalItems.length > 0) {
      sumTotalsByUnit = this.$typedStore.visualisationApp.sumFieldsByUnit({ leafNodes }, this.mapPopupTotalItems)
    }

    return this.$typedStore.visualisationApp.totalString({ sumTotalsByUnit })
  }
}

</script>
